import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import {
  Air,
  CropSquare,
  SelfImprovement,
  AccessibilityNew,
  BedtimeOutlined,
  PlayArrow,
  Schedule,
  Spa
} from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import PlayRecoveryRunner from 'components/play/PlayRecoveryRunner';
import PlayRecoveryDetailModal from 'components/play/recovery/PlayRecoveryDetailModal';
import { normalizePractice, fallbackPractices } from 'components/play/recovery/practiceUtils';
import { PLAY_RECOVERY_PRACTICES, MY_PLAY_RECOVERY_SESSIONS } from 'api/graphql/playRecovery';
import { formatDuration } from 'components/play/workoutEstimate';

const ICONS = { Air, CropSquare, SelfImprovement, AccessibilityNew, Spa };

/**
 * OTRIX Play — pantalla de un día SIN entrenamiento: prácticas de recuperación reales.
 * Las prácticas vienen del backend (editables por la entrenadora); si dev aún no tiene la
 * migración, cae a los bloques locales clásicos. Tocar una card abre el DETALLE (el timer
 * NUNCA arranca solo); cada card muestra su última sesión guardada.
 */
function PlayRestDayPanel() {
  const { t } = useTranslation(['play']);
  const [detail, setDetail] = useState(null);
  const [runner, setRunner] = useState(null); // { practice, tracks }

  const { data, loading } = useQuery(PLAY_RECOVERY_PRACTICES, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
  const { data: sessionsData } = useQuery(MY_PLAY_RECOVERY_SESSIONS, {
    variables: { first: 30 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  const practices = useMemo(() => {
    const remote = data?.playRecoveryPractices || [];
    if (remote.length > 0) return remote.map((p) => normalizePractice(t, p));
    return fallbackPractices(t);
  }, [data, t]);

  // Última sesión por práctica (por key de sistema o id de práctica custom).
  const lastByPractice = useMemo(() => {
    const map = new Map();
    (sessionsData?.myPlayRecoverySessions || []).forEach((s) => {
      const mapKey = s.practice_key || (s.practice_id ? `id-${s.practice_id}` : null);
      if (!mapKey || map.has(mapKey)) return; // ya vienen más recientes primero
      map.set(mapKey, s);
    });
    return map;
  }, [sessionsData]);

  const lastOf = (practice) =>
    lastByPractice.get(practice.key || (practice.id ? `id-${practice.id}` : '')) || null;

  const firstLoad = loading && !data;

  return (
    <>
      <PremiumCard>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                flex: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'gold.main',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'gold.soft'
              }}>
              <BedtimeOutlined />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={800} letterSpacing="-0.01em">
                {t('recovery.title', { ns: 'play' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('recovery.subtitle', { ns: 'play' })}
              </Typography>
            </Box>
          </Stack>

          {firstLoad && (
            <Grid container spacing={2}>
              {[0, 1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
                </Grid>
              ))}
            </Grid>
          )}

          {!firstLoad && (
            <Grid container spacing={2}>
              {practices.map((practice) => {
                const Icon = ICONS[practice.icon] || SelfImprovement;
                const last = lastOf(practice);
                const lastOk = last?.status === 'completed';

                return (
                  <Grid item xs={12} sm={6} key={practice.key || practice.id}>
                    <Stack
                      role="button"
                      tabIndex={0}
                      aria-label={practice.title}
                      onClick={() => setDetail(practice)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDetail(practice);
                        }
                      }}
                      spacing={1.25}
                      sx={{
                        height: '100%',
                        p: 2,
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'hairline.main',
                        backgroundColor: 'surface.raised',
                        boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
                        cursor: 'pointer',
                        transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1), border-color 220ms',
                        '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
                        '&:active': { transform: 'scale(0.98)' },
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
                      }}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Icon sx={{ color: 'gold.main', fontSize: 20, flex: 'none' }} />
                        <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                          {practice.title}
                        </Typography>
                        <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color: 'text.secondary', flex: 'none' }}>
                          <Schedule sx={{ fontSize: 13 }} />
                          <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {t('todayCard.minutes', { ns: 'play', count: practice.minutes })}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                        {practice.description}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ color: 'gold.main', fontWeight: 800 }}>
                          <PlayArrow sx={{ fontSize: 16 }} />
                          <Typography variant="caption" fontWeight={800}>
                            {t('recovery.viewDetail', { ns: 'play', defaultValue: 'Ver práctica' })}
                          </Typography>
                        </Stack>

                        {/* Última sesión guardada: verde si la completó, ámbar si fue parcial. */}
                        {last && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              color: lastOk ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg',
                              backgroundColor: lastOk ? 'sessionStatus.completed.bg' : 'sessionStatus.pending.bg'
                            }}>
                            {t('recovery.lastSession', {
                              ns: 'play',
                              time: formatDuration(last.total_seconds),
                              percent: last.completion_percent,
                              defaultValue: 'Última: {{time}} · {{percent}}%'
                            })}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </PremiumCard>

      <PlayRecoveryDetailModal
        practice={detail}
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        onStart={(tracks) => {
          setRunner({ practice: detail, tracks });
          setDetail(null);
        }}
      />

      <PlayRecoveryRunner
        practice={runner?.practice || null}
        musicTracks={runner?.tracks || null}
        open={Boolean(runner)}
        onClose={() => setRunner(null)}
      />
    </>
  );
}

export default PlayRestDayPanel;
